import random from 'canvas-sketch-util/random'
import * as PIXI from 'pixi.js'
import Player from './player.js'
import keyboard from './keyboard.js'
import Updater from './updater.js'
import orientation from './orientation.js'
import Platform from './platform.js'
import Score from './score.js'
import NyanSheep from './nyan-sheep.js'
import track from './track.js'

//
// Initialize keyboard
//

const left = keyboard(37)
const right = keyboard(39)

class Scene3 {
  constructor (game) {
    this.game = game
    this.isOver = false
    this.isPrepared = false
    this.isDestroyed = false
    this.nyanMode = false
    this.nyanCounter = 0

    // nyan mode 持續時間
    this.nyanModeDuration = 20000

    // 床墊出現機率的比重
    this.mattress = [
      { value: 'foam', weight: 12 },
      { value: 'cool', weight: 2 },
      { value: 'spring', weight: 1 }
    ],

    this.weightedEntites = [
      { value: 'platform', weight: 200 },
      { value: 'sheep', weight: 1 }
    ]

    // 物件的移動速度
    this.entitySpeed = 1

    this.nyanModeSpeed = 2.6
  }

  enter () {
    this.isOver = false
    this.isPrepared = false
    this.isDestroyed = false
    this.nyanMode = false
    this.nyanCounter = 0

    //
    // prepare header background
    //

    this.headerBG = new PIXI.Graphics()
    this.headerBG.beginFill(0x000000, 0.7)
    this.headerBG.drawRect(0, 0, this.game.app.renderer.realWidth, 45)
    this.headerBG.endFill()

    //
    // Update the background
    //

    this.game.background.ticker.stop()
    this.game.background.playBGM()

    this.game.background
      .set('#3f7089')
      .setTextureAlpha(0.2)

    this.game.player = new Player(this.game)
    const x = ~~(this.game.app.renderer.realWidth - this.game.player.view.width) / 2
    const y = ~~(this.game.app.renderer.realHeight - this.game.player.view.height) / 2
    this.game.player.setPosition(x, y)

    this.game.app.stage.addChild(this.headerBG)
    this.score = new Score(this.game)

    if (this.game.lastScore) {
      this.score.value = this.game.lastScore
      this.score.update()
    }

    this.score.draw(0, 9)
    this.game.app.stage.addChild(this.game.player.view)

    //
    // Change background by score
    //

    this.score.inTheMorning = () => {
      if (this.nyanMode) {
        return
      }

      this.game.background
        .set('#d8eaef')
        .setTextureAlpha(1)
    }

    this.score.atNoon = () => {
      if (this.nyanMode) {
        return
      }

      this.game.background
        .set('#fbddc7')
        .setTextureAlpha(0.5)
    }

    this.score.inTheNight = () => {
      if (this.nyanMode) {
        return
      }

      this.game.background
        .set('#3f7089')
        .setTextureAlpha(0.2)
    }

    left.press = orientation.gamma.onDecrease = gamma => {
      this.game.player.isMovingToRight = false
      this.game.player.isMovingToLeft = true

      if (gamma) {
        this.game.player.vGamma = gamma
      }
    }

    left.release = () => {
      this.game.player.isMovingToLeft = false
    }

    right.press = orientation.gamma.onIncrease = gamma => {
      this.game.player.isMovingToLeft = false
      this.game.player.isMovingToRight = true

      if (gamma) {
        this.game.player.vGamma = gamma
      }
    }

    right.release = () => {
      this.game.player.isMovingToRight = false
    }

    //
    // Generate Platforms first time
    //

    this.bubbles = this.bubbles || []
    this.entities = this.entities || []
    while (!this.isPrepared) {
      this.generateEntities()
    }

    //
    // Countdown
    //

    const bigNum3 = new PIXI.Sprite(PIXI.Texture.from('big_num_2'))
    const bigNum2 = new PIXI.Sprite(PIXI.Texture.from('big_num_1'))
    const bigNum1 = new PIXI.Sprite(PIXI.Texture.from('big_num_0'))
    const clock = new PIXI.Sprite(PIXI.Texture.from('clock'))

    bigNum3.anchor.set(0.5, 0.5)
    bigNum2.anchor.set(0.5, 0.5)
    bigNum1.anchor.set(0.5, 0.5)
    clock.anchor.set(0.5, 0)

    bigNum3.scale.set(0.25)
    bigNum2.scale.set(0.25)
    bigNum1.scale.set(0.25)
    clock.scale.set(0.25)

    bigNum3.x = this.game.app.renderer.realWidth / 2
    bigNum2.x = this.game.app.renderer.realWidth / 2
    bigNum1.x = this.game.app.renderer.realWidth / 2

    bigNum3.y = this.game.app.renderer.realHeight / 2
    bigNum2.y = this.game.app.renderer.realHeight / 2
    bigNum1.y = this.game.app.renderer.realHeight / 2

    let clockDirection = 1
    clock.x = this.game.app.renderer.realWidth / 2
    clock.y = 0
    clock.pivot.x = clock.width / 2
    clock.pivot.y = 0

    // draw overlay
    const overlay = new PIXI.Graphics()
    overlay.beginFill(0x000000, 0.85)
    overlay.drawRect(0, 0, this.game.app.renderer.realWidth, this.game.app.renderer.realHeight)
    overlay.endFill()

    this.game.app.stage.addChild(overlay)

    //
    // Define Counter
    //

    let secCounter = 3000
    const openingTicker = new PIXI.ticker.Ticker()
    const ease = 0.125
    openingTicker.add(deltaTime => {
      if (clockDirection === 1 && clock.rotation < 0.44) {
        let dr = 0.45 - Math.abs(clock.rotation)
        let vr = dr * ease
        clock.rotation += (vr * deltaTime)
      } else if (clockDirection === 1 && clock.rotation > 0.44) {
        clockDirection = 0
      }

      if (clockDirection === 0 && clock.rotation > -0.44) {
        let dr = 0.45 - Math.abs(clock.rotation)
        let vr = dr * ease
        clock.rotation -= (vr * deltaTime)
      } else if (clockDirection === 0 && clock.rotation < -0.44) {
        clockDirection = 1
      }

      secCounter -= openingTicker.elapsedMS
      if (secCounter > 1000 && secCounter < 2000) {
        this.game.app.stage.addChild(bigNum2)
        this.game.app.stage.removeChild(bigNum3)
      } else if (secCounter > 0) {
        this.game.app.stage.removeChild(bigNum2)
        this.game.app.stage.addChild(bigNum1)
      } else if (secCounter < 0) {
        this.game.app.stage.removeChild(bigNum1, overlay, clock)
        overlay.destroy()

        openingTicker.stop()
        openingTicker.destroy()

        this.onGameStart && this.onGameStart()

        // the first jump!
        this.game.player.jump()
        this.updater.start()

        this.game.background.ticker.start()
      }
    })

    if (!this.game.isPlayed) {
      this.game.isPlayed = true

      //
      // Tutorial
      //

      const tutorialKey1 = new PIXI.Sprite(PIXI.Texture.from('tutorialKey1'))
      const tutorialKey2 = new PIXI.Sprite(PIXI.Texture.from('tutorialKey2'))
      const tutorialPhone1 = new PIXI.Sprite(PIXI.Texture.from('tutorialPhone1'))
      const tutorialPhone2 = new PIXI.Sprite(PIXI.Texture.from('tutorialPhone2'))
      const tutorialPhone3 = new PIXI.Sprite(PIXI.Texture.from('tutorialPhone3'))
      const tutorialRuleHint = new PIXI.Sprite(PIXI.Texture.from('ruleHint'))
      const btnIKnow = new PIXI.Sprite(PIXI.Texture.from('btnIKnow'))

      const tutorialKey = new PIXI.extras.AnimatedSprite([
        PIXI.Texture.from('tutorialKey1'),
        PIXI.Texture.from('tutorialKey2')
      ])

      const tutorialPhone = new PIXI.extras.AnimatedSprite([
        PIXI.Texture.from('tutorialPhone1'),
        PIXI.Texture.from('tutorialPhone2'),
        PIXI.Texture.from('tutorialPhone3')
      ])

      btnIKnow.scale.set(0.25)
      tutorialKey.scale.set(0.37)
      tutorialPhone.scale.set(0.37)
      tutorialRuleHint.scale.set(0.25)

      btnIKnow.x = (this.game.app.renderer.realWidth / 2) - (btnIKnow.width / 2)
      tutorialKey.x = (this.game.app.renderer.realWidth / 2) - (tutorialKey.width / 2)
      tutorialPhone.x = (this.game.app.renderer.realWidth / 2) - (tutorialPhone.width / 2)
      tutorialRuleHint.x = (this.game.app.renderer.realWidth / 2) - (tutorialRuleHint.width / 2)

      tutorialPhone.y = 77
      tutorialKey.y = 317
      btnIKnow.y = 492
      tutorialRuleHint.y = 47

      this.game.app.stage.addChild(
        btnIKnow,
        tutorialKey,
        tutorialPhone,
        tutorialRuleHint
      )

      tutorialKey.animationSpeed = 0.0375
      tutorialPhone.animationSpeed = 0.075

      tutorialKey.play()
      tutorialPhone.play()

      const anime = TweenMax.to(btnIKnow, 0.4, {
        pixi: {
          y: 487
        },
        yoyo: true,
        repeat: -1
      })

      btnIKnow.interactive = true
      btnIKnow.buttonMode = true
      btnIKnow.on('pointerdown', () => {
        if (this.game.audioEnabled) {
          PIXI.sound.play('audioPop')
        }

        TweenMax.killTweensOf(anime)

        this.game.app.stage.removeChild(
          btnIKnow,
          tutorialKey,
          tutorialPhone,
          tutorialRuleHint
        )

        btnIKnow.interactive = false
        btnIKnow.buttonMode = false

        btnIKnow.destroy()
        tutorialKey.destroy()
        tutorialPhone.destroy()
        tutorialRuleHint.destroy()

        track('event', 'game_start', {
          category: this.game.id,
          label: 'click_i_know_button'
        })

        openingTicker.start()
        this.game.app.stage.addChild(bigNum3, clock)
        this.startedTimestamp = Date.now()
      })
    } else {
      // 這裏跟上面 code 是一樣的
      openingTicker.start()
      this.game.app.stage.addChild(bigNum3, clock)
    }

    //
    // Updater
    //

    let count = 0

    this.updater = new Updater(this.game)
    this.updater.nextTick(deltaTime => {

      if (this.game.player) {
        this.updatePlayer(deltaTime)
      }

      if (!this.isOver) {
        this.generateEntities(this.game)
      }

      // 遊戲即使結束，platforms 仍然要繼續往下跑，直到全部都跑出畫面外
      this.updatePlatforms(deltaTime)
      this.updateBubbles(deltaTime)

      if (this.nyanMode && this.nyanCounter > 0) {
        this.nyanCounter -= this.updater.ticker.elapsedMS
        if (this.nyanCounter >= 0 && this.nyanCounter <= 3500) {
          this.game.app.view.classList.add('leaving')
        }

        if (this.nyanCounter <= 0) {
          this.leaveNyanMode()
        }
      }
    })

    if (this.game.debugMode) {
      this.gameFolder = this.game.gui.addFolder('遊戲設定')
      this.gameFolder.add(this, 'nyanModeDuration', 2000, 40000).name('Nyan 模式持續時間').step(100)
      this.gameFolder.open()

      this.entitiesFolder = this.game.gui.addFolder('物件')
      this.entitiesFolder.add(this.weightedEntites[0], 'weight', 1, 100).name('床墊出現比重').step(1)
      this.entitiesFolder.add(this.weightedEntites[1], 'weight', 1, 100).name('羊出現比重').step(1)
      this.entitiesFolder.open()

      this.folder = this.game.gui.addFolder('床墊')
      this.folder.add(this.mattress[0], 'weight', 1, 100).name('棉花墊出現比重').step(1)
      this.folder.add(this.mattress[1], 'weight', 1, 100).name('記憶墊出現比重').step(1)
      this.folder.add(this.mattress[2], 'weight', 1, 100).name('彈簧墊出現比重').step(1)
      this.folder.add(this, 'entitySpeed', 0.1, 4).name('加速倍率').step(0.1)
      this.folder.open()

      this.scoreFolder = this.game.gui.addFolder('分數增加的速度')
      this.scoreFolder.add(this.score, 'times', 1, 10).name('倍率').step(1)
      this.scoreFolder.open()
    }
  }

  leave () {
    this.destroy()
  }

  destroy () {
    if (this.isDestroyed) {
      return
    }

    if (this.entities.length > 0) {
      this.entities.forEach(entity => {
        this.game.app.stage.removeChild(entity.view)
        entity.view.destroy()
      })
    }

    if (this.bubbles.length > 0) {
      this.bubbles.forEach(bubble => {
        this.game.app.stage.removeChild(bubble.view)
        bubble.view.destroy()
      })
    }

    this.game.player.destroy()
    this.game.player = null

    right.press = null
    left.press = null
    right.release = null
    left.release = null
    orientation.gamma.onIncrease = null
    orientation.gamma.onDecrease = null

    this.updater.destroy()
    this.updater = null
    this.entities = []
    this.bubbles = []
    this.game.cameraDelta = 0
    this.isDestroyed = true

    this.startedTimestamp = null
    this.endingTimestamp = null

    if (this.game.gui) {
      this.game.gui.removeFolder(this.gameFolder)
      this.game.gui.removeFolder(this.folder)
      this.game.gui.removeFolder(this.entitiesFolder)
      this.game.gui.removeFolder(this.scoreFolder)
    }
  }

  // 不斷產生新的床墊往下掉
  generatePlatforms (lastEntity, offset) {
    let type = this.nyanMode ? 'sheep' : random.weightedSet(this.mattress)
    let speed = this.nyanMode ? this.nyanModeSpeed : 2
    const platform = new Platform(this.game, type, speed)
    const x = (platform.view.width / 2) + ~~(random.value() * (this.game.app.renderer.realWidth - platform.view.width))
    const y = lastEntity ? lastEntity.view.y - ~~(platform.view.height * offset)
                           : this.game.app.renderer.realHeight - (platform.view.height * 2)
    platform.setPosition(x, y)
    platform.draw()
    this.entities.push(platform)
  }

  // 隨機生出 Nyan Sheep
  generateSheep (lastEntity, offset) {
    if (this.score.value < 500 || !lastEntity) {
      return
    }

    const sheep = new NyanSheep(this.game)
    const x = ~~(random.value() * (this.game.app.renderer.realWidth - sheep.view.width))
    const y = -sheep.view.height
    sheep.setPosition(x, y)
    sheep.draw()
    this.entities.push(sheep)
  }

  generateEntities () {
    const lastEntity = this.entities[this.entities.length - 1]

    // 7 天拉開床墊間距
    const delta = this.score.value / (60 * 60 * 24 * 15)
    const offset = 2 + (delta < 1 ? 1 * delta : 1) + (4 * Math.random())
    if (lastEntity && !(lastEntity.view.y > (lastEntity.view.height * offset))) {
      this.isPrepared = true
      return
    }

    const entityType = random.weightedSet(this.weightedEntites)

    if (entityType === 'sheep' && !this.nyanMode) {
      this.generateSheep(lastEntity, offset)
    }

    if (entityType === 'platform') {
      this.generatePlatforms(lastEntity, offset)
    }
  }

  // 更新玩家往上跳，Jump！
  updatePlayer (deltaTime) {
    if (!this.game.player) {
      return
    }

    if (this.game.player.isJumping) {
      // 用玩家的跳躍加速度來增加分數
      const coe = this.nyanMode ? 8 : 1
      this.score.increase(this.game.player.getJumpVelocity() * coe * deltaTime)
      this.score.update()

      // 1 base
      // 1.2 delta
      // 2.2 max
      if (this.entitySpeed < 1.9) {
        this.entitySpeed = 1 + 0.9 * (this.score.value / (60 * 60 * 24 * 10))
      }

      this.game.player.jumping(() => {
        if (this.game.player.view.y <= 150) {
          this.game.player.freeze()
          this.game.cameraDelta = this.game.player.getJumpVelocity()
        }
      }, deltaTime)
    }

    if (this.game.player.isFalling) {
      this.game.player.falling(() => {
        this.game.player.unfreeze()
      }, deltaTime)
    }

    if (this.game.player.bubble.any) {
      this.game.player.bubble.update(this.game.player.view.x, this.game.player.view.y)
    }

    if (!this.isOver && isReachedBottom(this.game.player, this.game)) {
      if (this.game.player.isUnbeatable) {
        this.game.player.jump()
      } else if (this.nyanMode) {
        this.game.player.setPosition(this.game.player.view.x, 0)
      } else {
        this.isOver = true

        this.game.background.fadeOutBGM()

        this.game.app.stage.removeChild(this.headerBG)
        this.headerBG.destroy()
        this.headerBG = null

        this.endingTimestamp = Date.now()
        this.onGameOver && this.onGameOver(this.score.value, this.game.player)

        this.score.destroy()
        this.score = null

        return
      }
    }

    if (this.game.player.isMovingToLeft) {
      // 穿越左邊界線到右邊
      if ((this.game.player.view.x < 0)) {
        this.game.player.setPosition(this.game.app.renderer.realWidth, this.game.player.view.y)
      }

      this.game.player.moveLeft(deltaTime)
    }

    if (this.game.player.isMovingToRight) {
      // 穿越右邊界線到左邊
      if (this.game.player.view.x > this.game.app.renderer.realWidth) {
        this.game.player.setPosition(0, this.game.player.view.y)
      }

      this.game.player.moveRight(deltaTime)
    }

    this.game.player.updateZs(deltaTime)
    this.game.player.updateBubbles()
  }

  // 更新 Entity 往下掉
  updatePlatforms (deltaTime) {
    if (this.isOver && this.entities.length === 0) {
      return this.onLeaveScene3()
    }

    this.entities.forEach((entity, index) => {
      entity.moveDown(this.game.cameraDelta, this.entitySpeed, deltaTime)

      if (entity.view.y > (this.game.app.renderer.realHeight)) {
        entity.view.destroy()
        this.entities.splice(index, 1)
      } else {
        this.checkCollision(this.game.player, entity)
      }
    })
  }

  // 更新 bubbles
  updateBubbles (deltaTime) {
    if (this.bubbles.length > 0) {
      this.bubbles.forEach((bubble, index) => {
        if (bubble.isCompleted) {
          this.bubbles.splice(index, 1)
          bubble.destroy()
          return
        } else {
          bubble.update(deltaTime)
          bubble.rise()
        }
      })
    }
  }

  // 檢查是否踩到床墊並且決定以何種方式彈跳
  checkCollision (source, target) {
    if (source && source.isFalling && source.isCollision(target)) {
      if (target.type === 'spring') {
        source.superJump()
      } else if (target.type === 'cool') {
        source.fly()
      } else if (target instanceof NyanSheep) {
        this.enterNyanMode()
        source.jump()
      } else {
        source.jump()
      }
      target.quake && target.quake()
    }
  }

  enterNyanMode () {
    if (!this.nyanMode && this.game.audioEnabled) {
      this.game.background.pauseBGM()
      const sound = PIXI.sound.play('audioNyanMode')
      sound.volume = 0.2
    }

    this.nyanMode = true
    this.nyanCounter = this.nyanModeDuration
    this.game.app.view.classList.add('nyan-mode')
    this.game.background.setTextureAlpha(1)

    this.entities.forEach(entity => {
      if (entity instanceof Platform) {
        entity.transformTo('sheep')
        entity.speedUpTo(this.nyanModeSpeed)
        entity.generateBubbles(20).forEach(bubble => {
          this.bubbles.push(bubble)
        })
      }

      if (entity instanceof NyanSheep) {
        entity.speedUpTo(this.nyanModeSpeed)
      }
    })
  }

  leaveNyanMode () {
    this.nyanMode = false
    this.nyanCounter = 0
    this.game.app.view.classList.remove('nyan-mode')
    this.game.app.view.classList.remove('leaving')

    if (this.game.audioEnabled) {
      PIXI.sound.stop('audioNyanMode')
    }
    this.game.background.resumeBGM()

    this.entities.forEach(entity => {
      if (entity instanceof Platform) {
        entity.transformTo('foam')
        entity.speedUpTo(2)
      }

      if (entity instanceof NyanSheep) {
        entity.speedUpTo(2)
      }
    })
  }
}

//
// Private Methods
//

// 是否掉到畫面之外了，GG！
function isReachedBottom (player, game) {
  if (player.isUnbeatable) {
    return (player.view.y - (player.view.height / 2) + player.view.height) >= game.app.renderer.realHeight
  }

  return (player.view.y - (player.view.height / 2)) >= game.app.renderer.realHeight
}

export default Scene3
